import { useState } from "react";
import {
  Typography,
  Box,
  Backdrop,
  Button,
  Avatar,
  CardMedia,
  TextField,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import ApiService from "../../modules/APIService.interceptors";

const toInputDate = (dateLike) => {
  if (!dateLike) return "";
  const d = new Date(dateLike);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const fromInputDate = (value) => {
  return value ? new Date(value + "T00:00:00") : null;
};

const EditLiveModal = ({
  live,
  onCancelEdit,
  isEditing,
  setIsEditing,
  onSave,
}) => {
  const [thumbnail, setThumbnail] = useState(live.thumbnail);
  const [imageFile, setImageFile] = useState(live.thumbnail);
  const [title, setTitle] = useState(live.title);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [loader, setLoader] = useState(false);

  // 🗓️ Solo fecha (día)
  const [streamingDateInput, setStreamingDateInput] = useState(
    toInputDate(live.streamingDate)
  );

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) setThumbnail(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleConfirmEdit = async () => {
    const changedThumb = imageFile !== live.thumbnail;
    const changedTitle = title.trim() !== live.title;
    const newStreamingDate = fromInputDate(streamingDateInput);
    const changedDate =
      (newStreamingDate &&
        new Date(live.streamingDate).toDateString() !==
          newStreamingDate.toDateString()) ||
      (!live.streamingDate && !!newStreamingDate);

    if (!changedThumb && !changedTitle && !changedDate) {
      setIsEditing(false);
      return;
    }

    setLoader(true);
    try {
      if (changedThumb && imageFile instanceof File) {
        const formData = new FormData();
        formData.append("thumbnail", imageFile);
        await ApiService.updateLiveThumbnail(live.id, formData);
      }

      if (changedTitle || changedDate) {
        const payload = {};
        if (changedTitle) payload.title = title.trim();
        if (changedDate && newStreamingDate)
          payload.streamingDate = newStreamingDate;
        await ApiService.updateLiveName(live.id, payload);
      }

      const updated = {
        ...live,
        title: changedTitle ? title.trim() : live.title,
        streamingDate:
          changedDate && newStreamingDate
            ? newStreamingDate.toISOString()
            : live.streamingDate,
        thumbnail: changedThumb ? thumbnail : live.thumbnail,
      };
      if (typeof onSave === "function") onSave(updated);

      setIsEditing(false);
    } catch (error) {
      console.error("Error al actualizar:", error);
    } finally {
      setLoader(false);
    }
  };

  return (
    <Backdrop
      sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 1 }}
      open={isEditing}
    >
      <Box
        sx={{
          background: "white",
          minWidth: "45vh",
          maxHeight: "55vh",
          borderRadius: 5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "3.5vh 1.6vh",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            id="upload-image"
            onChange={handleImageChange}
          />
          <label htmlFor="upload-image">
            <IconButton component="span" sx={{ backgroundColor: "white" }}>
              <EditIcon color="primary" />
            </IconButton>
          </label>

          <Avatar sx={{ width: 140, height: 140, mb: "2vh" }}>
            <CardMedia
              component="img"
              sx={{ width: "100%", height: "100%" }}
              image={thumbnail}
              alt="Miniatura"
            />
          </Avatar>

          {isEditingTitle ? (
            <TextField
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => setIsEditingTitle(false)}
              autoFocus
              variant="standard"
              sx={{ mb: "2.5vh" }}
              label="Título"
            />
          ) : (
            <Typography
              sx={{
                color: "GrayText",
                mb: "2.5vh",
                cursor: "pointer",
                borderBottom: 2,
                borderBottomColor: "#D0749B",
              }}
              variant="h6"
              onClick={() => setIsEditingTitle(true)}
            >
              {title}
            </Typography>
          )}

          {/* 📅 Solo día */}
          <TextField
            label="Fecha de transmisión"
            type="date"
            value={streamingDateInput}
            onChange={(e) => setStreamingDateInput(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 200 }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            width: "80%",
            mt: 3,
          }}
        >
          <Button
            variant="contained"
            onClick={handleConfirmEdit}
            color="primary"
          >
            Confirmar
          </Button>
          <Button variant="outlined" onClick={onCancelEdit} color="primary">
            Cancelar
          </Button>
        </Box>
      </Box>

      <Backdrop
        sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 2 }}
        open={loader}
      >
        <CircularProgress color="primary" />
      </Backdrop>
    </Backdrop>
  );
};

export default EditLiveModal;
